(function () {
  if (!window.customElements || customElements.get('editorial-collection')) return;

  const STORAGE_KEY = 'jakobsimon:collection-columns';
  const FEATURE_CLASS = 'collection-editorial__card--feature';

  /*
   * Lays the cards out the way CSS grid's dense auto-placement does: each card takes the
   * first slot, scanning row by row, where its span fits. Returns the occupied cells.
   */
  function placeCards(spans, columns) {
    const rows = [];
    const isFree = (row, col) => !rows[row] || !rows[row][col];

    spans.forEach(([colSpan, rowSpan]) => {
      for (let row = 0; ; row++) {
        for (let col = 0; col + colSpan <= columns; col++) {
          let fits = true;
          for (let r = row; r < row + rowSpan && fits; r++) {
            for (let c = col; c < col + colSpan && fits; c++) fits = isFree(r, c);
          }
          if (!fits) continue;

          for (let r = row; r < row + rowSpan; r++) {
            rows[r] = rows[r] || new Array(columns).fill(false);
            for (let c = col; c < col + colSpan; c++) rows[r][c] = true;
          }
          return;
        }
      }
    });

    return rows;
  }

  // A gap is an empty cell anywhere but the tail of the last row.
  function hasGap(rows, columns) {
    const last = rows.length - 1;
    for (let r = 0; r < rows.length; r++) {
      for (let c = 0; c < columns; c++) {
        if (rows[r] && rows[r][c]) continue;
        if (r < last) return true;
        if (rows[r].slice(c + 1).some(Boolean)) return true;
      }
    }
    return false;
  }

  class EditorialCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      this.content = this.querySelector('[data-collection-content]');

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.balanceFeatures = this.balanceFeatures.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      window.addEventListener('popstate', this.onPopState);

      this.breakpoint = window.matchMedia('(min-width: 769px)');
      this.breakpoint.addEventListener('change', this.balanceFeatures);

      this.applyColumns(this.readColumns(), false);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      window.removeEventListener('popstate', this.onPopState);
      if (this.breakpoint) this.breakpoint.removeEventListener('change', this.balanceFeatures);
      if (this.controller) this.controller.abort();
    }

    readColumns() {
      const fromUrl = new URL(window.location.href).searchParams.get('columns');
      if (fromUrl) return fromUrl;

      let stored = null;
      try {
        stored = window.localStorage.getItem(STORAGE_KEY);
      } catch (error) {
        stored = null;
      }
      return stored || this.getAttribute('data-default-columns') || '4';
    }

    applyColumns(value, updateUrl) {
      const allowed = (this.getAttribute('data-column-options') || '2,3,4').split(',');
      const columns = allowed.indexOf(String(value)) === -1 ? allowed[allowed.length - 1] : String(value);

      this.setAttribute('data-columns', columns);

      this.querySelectorAll('[data-columns-button]').forEach((button) => {
        const isCurrent = button.getAttribute('data-columns-button') === columns;
        button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
      });

      this.balanceFeatures();

      try {
        window.localStorage.setItem(STORAGE_KEY, columns);
      } catch (error) {
        /* storage unavailable */
      }

      if (!updateUrl) return;

      const url = new URL(window.location.href);
      url.searchParams.set('columns', columns);
      window.history.replaceState({ collection: true }, '', url.toString());
    }

    /*
     * A large card spans two columns, and two rows on desktop. Where the products run out or the
     * column count leaves no room beside it, it would leave an empty slot, so the last large cards
     * fall back to standard ones until the grid closes up. Every view starts from the original set,
     * so switching back to a roomier view restores them.
     */
    balanceFeatures() {
      const grid = this.querySelector('.collection-editorial__grid');
      if (!grid) return;

      const cards = Array.prototype.slice.call(grid.children);
      const originals = cards.filter((card) => card.hasAttribute('data-feature-card'));
      if (originals.length === 0) return;

      originals.forEach((card) => card.classList.add(FEATURE_CLASS));

      const columns = window.getComputedStyle(grid).gridTemplateColumns.split(' ').length;
      // `grid-row: span 2` resolves to a span on the row start, so read both ends.
      const featureStyle = window.getComputedStyle(originals[0]);
      const rowLines = featureStyle.gridRowStart + ' ' + featureStyle.gridRowEnd;
      const rowSpan = parseInt((rowLines.match(/span (\d+)/) || [])[1], 10) || 1;
      const colSpan = Math.min(2, columns);

      let featured = originals.slice();
      while (featured.length > 0) {
        const spans = cards.map((card) => (featured.indexOf(card) === -1 ? [1, 1] : [colSpan, rowSpan]));
        if (!hasGap(placeCards(spans, columns), columns)) break;
        featured.pop().classList.remove(FEATURE_CLASS);
      }
    }

    onClick(event) {
      const columnsButton = event.target.closest('[data-columns-button]');
      if (columnsButton) {
        event.preventDefault();
        this.applyColumns(columnsButton.getAttribute('data-columns-button'), true);
        return;
      }

      const link = event.target.closest('[data-collection-link]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();
      this.navigate(link.getAttribute('href'), link.hasAttribute('data-scroll-top'));
    }

    onChange(event) {
      const select = event.target.closest('[data-collection-sort]');
      if (!select) return;

      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      this.navigate(url.toString(), false);
    }

    onPopState() {
      this.render(window.location.href, false);
      this.applyColumns(this.readColumns(), false);
    }

    navigate(href, scrollTop) {
      const url = new URL(href, window.location.origin);
      const columns = this.getAttribute('data-columns');
      if (columns) url.searchParams.set('columns', columns);

      window.history.pushState({ collection: true }, '', url.toString());
      this.render(url.toString(), scrollTop);
    }

    render(href, scrollTop) {
      const url = new URL(href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.setAttribute('aria-busy', 'true');

      fetch(url.toString(), { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const fresh = html.querySelector('[data-collection-content]');
          if (!fresh || !this.content) return;

          this.content.innerHTML = fresh.innerHTML;
          this.applyColumns(this.readColumns(), false);

          if (scrollTop) {
            const top = this.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          window.location.href = href;
        })
        .finally(() => {
          this.removeAttribute('aria-busy');
        });
    }
  }

  customElements.define('editorial-collection', EditorialCollection);
})();
